// Queues component with login-as-customer support and proper filtering
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import errorLogger from '../../services/errorLogger.service';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Tooltip,
  Switch,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Info as InfoIcon,
  MusicNote as MusicNoteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useLocation } from 'react-router-dom';

// Create component logger
const logger = errorLogger.createComponentLogger('QueuesAsCustomerNew');

interface Queue {
  name: string;
  musiconhold: string;
  moh_files: string;
  announcement_file: number | null;
  announcement_freq: number;
  timeout: number;
  pbxapp: number;
  pbxdigits: string;
  pbxdesc: string;
  RecordID: string;
  duration: number;
  member_count: number;
  active_member_count: number;
}

interface QueueRecording {
  RecordID: number;
  Name: string;
  usage_type: 'moh' | 'announcement' | 'both';
}

interface QueueMember {
  uniqueid: number;
  membername: string;
  extension: string;
  fullname: string;
  penalty: number;
  paused: number;
}

interface Extension {
  extension: string;
  fullname: string;
  is_member?: number;
  uniqueid?: number;
  paused?: number;
}

interface OverflowAction {
  action_key: number;
  action_name: string;
  requires_target: boolean;
}

interface OverflowTarget {
  value: string;
  name: string;
}

function QueuesAsCustomerNew() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const customerFilter = useCustomerFilter();
  const location = useLocation();

  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });

  // Track last fetched customer ID to prevent unnecessary refetches
  const [lastFetchedCustomerId, setLastFetchedCustomerId] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [queues, setQueues] = useState<Queue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<Queue | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    musiconhold: 'default',
    moh_files: [] as number[], // Array of RecordIDs for MOH playlist
    announcement_file: null as number | null,
    announcement_freq: 60,
    timeout: 300, // 5 minutes in seconds
    pbxapp: 9,
    pbxdigits: '',
    pbxdesc: '',
    recordID: '',
    duration: 60,
  });

  // Members state
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [members, setMembers] = useState<QueueMember[]>([]);

  // Options data
  const [overflowActions, setOverflowActions] = useState<OverflowAction[]>([]);
  const [overflowTargets, setOverflowTargets] = useState<OverflowTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [queueRecordings, setQueueRecordings] = useState<QueueRecording[]>([]);

  // Helper to get customer filter params
  const getCustomerParams = () => {
    const params: any = {};
    // SECURITY: ALWAYS pass customerId to prevent data leakage
    if (isLoginAsCustomer && member?.customerID) {
      params.customerId = member.customerID;
      logger.debug(`LOGIN AS MODE - Using customerID: ${member.customerID}`);
    } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
      params.customerId = customerFilter.customerId;
      logger.debug(`FILTER MODE - Using customerID: ${customerFilter.customerId}`);
    } else if (member?.customerID) {
      // Regular customer login - MUST pass their customer ID
      params.customerId = member.customerID;
      logger.debug(`CUSTOMER LOGIN - Using customerID: ${member.customerID}`);
    } else {
      logger.error('SECURITY WARNING: No customer ID available!');
    }
    return params;
  };

  const fetchQueues = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting fetch');
      return;
    }

    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }

    if (!member) return;

    try {
      setLoading(true);
      setError(null);

      const params = getCustomerParams();
      logger.debug('Fetching queues with params:', params);

      const response = await api.get('/endpoints/queues', {
        params: params
      });

      if (response.data.success) {
        setQueues(response.data.queues || []);
        // Update last fetched customer ID
        if (params.custid) {
          setLastFetchedCustomerId(params.custid);
        }
      } else {
        setError(response.data.message || 'Failed to fetch queues');
      }
    } catch (err: any) {
      logger.error('Failed to fetch queues:', err);
      setError('Failed to load queues');
    } finally {
      setLoading(false);
    }
  };

  const fetchQueueRecordings = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting recordings fetch');
      return;
    }

    try {
      const params = getCustomerParams();
      const response = await api.get('/endpoints/queues', {
        params: {
          action: 'queue_recordings',
          ...params
        }
      });

      if (response.data.success) {
        setQueueRecordings(response.data.recordings || []);
      }
    } catch (err: any) {
      logger.error('Failed to fetch queue recordings:', err);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await api.get('/endpoints/overflow/applications');
      if (response.data.success && response.data.data) {
        // Transform the data format from {app_id, app_name, app_active} to {action_key, action_name, requires_target}
        const actions = response.data.data.map((app: any) => ({
          action_key: app.app_id,
          action_name: app.app_name,
          requires_target: ![9, 10, 15].includes(app.app_id) // End Call, Repeat Prompt, Directory don't need targets
        }));
        setOverflowActions(actions);
      }
    } catch (err) {
      logger.error('Failed to fetch overflow actions:', err);
      const defaultActions = [
        { action_key: 7, action_name: 'Voicemail', requires_target: true },
        { action_key: 9, action_name: 'End Call', requires_target: false },
      ];
      setOverflowActions(defaultActions);
    }
  };

  const fetchOverflowTargets = async (appId: number) => {
    setLoadingTargets(true);
    try {
      const params = getCustomerParams();
      const response = await api.get(`/endpoints/overflow/targets/${appId}`, {
        params: params.customerId ? { customerId: params.customerId } : {}
      });

      if (response.data.success && response.data.data) {
        setOverflowTargets(response.data.data || []);
      } else {
        setOverflowTargets([]);
      }
    } catch (err) {
      logger.error('Failed to fetch overflow targets:', err);
      setOverflowTargets([]);
    } finally {
      setLoadingTargets(false);
    }
  };

  const fetchExtensions = async (queueName?: string) => {
    try {
      const params = getCustomerParams();
      const response = await api.get('/endpoints/queues', {
        params: {
          action: 'available_extensions',
          queue_name: queueName || '',
          ...params
        }
      });

      if (response.data.success) {
        setExtensions(response.data.extensions || []);
      }
    } catch (err) {
      logger.error('Failed to fetch extensions:', err);
    }
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      musiconhold: 'default',
      moh_files: [],
      announcement_file: null,
      announcement_freq: 60,
      timeout: 300, // 5 minutes in seconds
      pbxapp: 9,
      pbxdigits: '',
      pbxdesc: '',
      recordID: '',
      duration: 60,
    });
    setAddDialogOpen(true);
  };

  const handleEdit = (queue: Queue) => {
    setSelectedQueue(queue);
    // Parse moh_files from comma-separated string to array of numbers
    const mohFilesArray = queue.moh_files
      ? queue.moh_files.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
      : [];

    setFormData({
      name: queue.name,
      musiconhold: queue.musiconhold,
      moh_files: mohFilesArray,
      announcement_file: queue.announcement_file || null,
      announcement_freq: queue.announcement_freq || 60,
      timeout: queue.timeout,
      pbxapp: queue.pbxapp,
      pbxdigits: queue.pbxdigits || '',
      pbxdesc: queue.pbxdesc || '',
      recordID: queue.RecordID || '',
      duration: queue.duration,
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (queue: Queue) => {
    setSelectedQueue(queue);
    setDeleteDialogOpen(true);
  };

  const handleManageMembers = async (queue: Queue) => {
    setSelectedQueue(queue);
    await fetchExtensions(queue.name);
    setMembersDialogOpen(true);
  };

  const handleSaveQueue = async () => {
    try {
      setError(null);

      if (!formData.name) {
        setError('Queue name is required');
        return;
      }

      const isEdit = editDialogOpen;
      const method = isEdit ? 'put' : 'post';

      // Build URL with customer params as query string (like UsersAsExtensions does)
      const params = getCustomerParams();
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString ? `/endpoints/queues?${queryString}` : '/endpoints/queues';

      // Convert moh_files array to CSV string for backend
      const payload = {
        ...formData,
        moh_files: formData.moh_files.join(',')
      };

      const response = await api[method](endpoint, payload);

      if (response.data.success) {
        setSuccess(isEdit ? 'Queue updated successfully' : 'Queue created successfully');
        setAddDialogOpen(false);
        setEditDialogOpen(false);
        fetchQueues();
      } else {
        setError(response.data.message || 'Failed to save queue');
      }
    } catch (err: any) {
      logger.error('Failed to save queue:', err);
      setError(err.response?.data?.message || 'Failed to save queue');
    }
  };

  const handleDeleteQueue = async () => {
    if (!selectedQueue) return;

    try {
      const params = getCustomerParams();
      const response = await api.delete('/endpoints/queues', {
        data: {
          action: 'delete_queue',
          name: selectedQueue.name,
          ...params
        }
      });

      if (response.data.success) {
        setSuccess('Queue deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedQueue(null);
        fetchQueues();
      } else {
        setError(response.data.message || 'Failed to delete queue');
      }
    } catch (err: any) {
      logger.error('Failed to delete queue:', err);
      setError('Failed to delete queue');
    }
  };

  const handleToggleMember = async (extension: Extension) => {
    if (!selectedQueue) return;

    try {
      const params = getCustomerParams();
      if (extension.is_member) {
        // Remove member
        const response = await api.delete('/endpoints/queues', {
          data: {
            action: 'remove_member',
            uniqueid: extension.uniqueid,
            ...params
          }
        });

        if (response.data.success) {
          setSuccess('Member removed successfully');
          fetchExtensions(selectedQueue.name);
          fetchQueues();
        }
      } else {
        // Add member
        const response = await api.post('/endpoints/queues', {
          action: 'add_member',
          queue_name: selectedQueue.name,
          extension: extension.extension,
          penalty: 1,
          ...params
        });

        if (response.data.success) {
          setSuccess('Member added successfully');
          fetchExtensions(selectedQueue.name);
          fetchQueues();
        }
      }
    } catch (err: any) {
      logger.error('Failed to toggle member:', err);
      setError('Failed to update member');
    }
  };

  const handleTogglePause = async (extension: Extension) => {
    if (!selectedQueue || !extension.uniqueid) return;

    try {
      const params = getCustomerParams();
      const newPausedState = extension.paused ? 0 : 1;
      const response = await api.post('/endpoints/queues', {
        action: 'toggle_pause',
        uniqueid: extension.uniqueid,
        paused: newPausedState,
        ...params
      });

      if (response.data.success) {
        setSuccess(newPausedState ? 'Member paused' : 'Member activated');
        fetchExtensions(selectedQueue.name);
        fetchQueues();
      }
    } catch (err: any) {
      logger.error('Failed to toggle pause:', err);
      setError('Failed to update member status');
    }
  };

  // Main effect for loading queues
  useEffect(() => {
    if (!member) return;

    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');

    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Have login token but no session data - waiting...');
      return;
    }

    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }

    // Prevent duplicate fetches for same customer
    if (member.customerID === lastFetchedCustomerId) {
      logger.debug('Already fetched for this customer, skipping');
      return;
    }

    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchQueues();
      fetchOptions();
      fetchQueueRecordings();
    }, 200);

    return () => clearTimeout(timer);
  }, [member?.customerID, isLoginAsCustomer]); // Re-fetch when customer ID or login-as state changes

  // Fetch targets when overflow app changes
  useEffect(() => {
    if ((addDialogOpen || editDialogOpen) && formData.pbxapp && overflowActions.length > 0) {
      const action = overflowActions.find(a => a.action_key === formData.pbxapp);
      if (action?.requires_target) {
        fetchOverflowTargets(formData.pbxapp);
      }
    }
  }, [addDialogOpen, editDialogOpen, formData.pbxapp, overflowActions]);

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h1">
              Call Queues
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<MusicNoteIcon />}
                onClick={() => navigate('/queue-announcements')}
              >
                Manage Audio
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
              >
                Add Queue
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchQueues}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {loading ? (
            <Typography>Loading queues...</Typography>
          ) : queues.length === 0 ? (
            <Typography>No queues found. Click "Add Queue" to create one.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Timeout (min)</TableCell>
                    <TableCell>Music on Hold</TableCell>
                    <TableCell>Members</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queues.map((queue) => (
                    <TableRow key={queue.name}>
                      <TableCell>{queue.name}</TableCell>
                      <TableCell>{Math.round(queue.timeout / 60)} Minutes</TableCell>
                      <TableCell>{queue.musiconhold}</TableCell>
                      <TableCell>
                        <Chip
                          icon={<GroupIcon />}
                          label={`${queue.active_member_count}/${queue.member_count}`}
                          size="small"
                          color={queue.active_member_count > 0 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Manage Members">
                          <IconButton size="small" onClick={() => handleManageMembers(queue)}>
                            <GroupIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(queue)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(queue)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={addDialogOpen || editDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editDialogOpen ? 'Edit Queue' : 'Add Queue'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Queue Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={editDialogOpen}
                size="small"
                helperText="e.g., 1002-sales"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Queue Timeout</InputLabel>
                <Select
                  value={formData.timeout}
                  onChange={(e) => setFormData({ ...formData, timeout: Number(e.target.value) })}
                  label="Queue Timeout"
                >
                  {[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120].map((mins) => (
                    <MenuItem key={mins} value={mins * 60}>
                      {mins} minutes
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Maximum number of minutes until the overflow action selected below
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                Audio Settings
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Music On Hold</InputLabel>
                <Select
                  value={formData.moh_files.length === 0 ? 'system_generated' : 'custom_files'}
                  onChange={(e) => {
                    if (e.target.value === 'system_generated') {
                      setFormData({ ...formData, moh_files: [] });
                    }
                  }}
                  label="Music On Hold"
                >
                  <MenuItem value="system_generated">System Generated</MenuItem>
                  <MenuItem value="custom_files">Custom Files (select below)</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {formData.moh_files.length === 0
                  ? 'Using default system music on hold'
                  : 'Files play in sequence and loop'
                }
              </Typography>
            </Grid>

            {formData.moh_files.length > 0 || queueRecordings.filter(r => r.usage_type === 'moh' || r.usage_type === 'both').length > 0 ? (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={queueRecordings.filter(r => r.usage_type === 'moh' || r.usage_type === 'both')}
                  getOptionLabel={(option) => option.Name}
                  value={queueRecordings.filter(r => formData.moh_files.includes(r.RecordID))}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      moh_files: newValue.map(v => v.RecordID)
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Music Files"
                      size="small"
                      placeholder={formData.moh_files.length === 0 ? "Select files to use custom music" : ""}
                    />
                  )}
                  disabled={formData.moh_files.length === 0 && queueRecordings.filter(r => r.usage_type === 'moh' || r.usage_type === 'both').length === 0}
                />
              </Grid>
            ) : null}

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={queueRecordings.filter(r => r.usage_type === 'announcement' || r.usage_type === 'both')}
                getOptionLabel={(option) => option.Name}
                value={queueRecordings.find(r => r.RecordID === formData.announcement_file) || null}
                onChange={(_, newValue) => {
                  setFormData({
                    ...formData,
                    announcement_file: newValue ? newValue.RecordID : null
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Periodic Announcement"
                    size="small"
                    helperText="Interrupts MOH at intervals"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Announcement Frequency</InputLabel>
                <Select
                  value={formData.announcement_freq}
                  onChange={(e) => setFormData({ ...formData, announcement_freq: Number(e.target.value) })}
                  label="Announcement Frequency"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((mins) => (
                    <MenuItem key={mins} value={mins * 60}>
                      {mins} {mins === 1 ? 'minute' : 'minutes'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                How often to play the announcement
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
                If Unanswered (Overflow)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={formData.pbxapp}
                  onChange={(e) => {
                    const appId = Number(e.target.value);
                    setFormData({ ...formData, pbxapp: appId, pbxdigits: '', pbxdesc: '' });
                    setOverflowTargets([]);
                    const action = overflowActions.find(a => a.action_key === appId);
                    if (action?.requires_target) {
                      fetchOverflowTargets(appId);
                    }
                  }}
                  label="Action"
                >
                  {overflowActions.map((action) => (
                    <MenuItem key={action.action_key} value={action.action_key}>
                      {action.action_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {overflowActions.find(a => a.action_key === formData.pbxapp)?.requires_target && (
              <Grid item xs={12} md={6}>
                {loadingTargets ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Target</InputLabel>
                    <Select value="" disabled label="Target">
                      <MenuItem value="">Loading...</MenuItem>
                    </Select>
                  </FormControl>
                ) : overflowTargets.length > 0 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Target</InputLabel>
                    <Select
                      value={formData.pbxdigits || overflowTargets[0]?.value || ''}
                      onChange={(e) => {
                        const selectedTarget = overflowTargets.find(t => t.value === e.target.value);
                        setFormData({
                          ...formData,
                          pbxdigits: e.target.value,
                          pbxdesc: selectedTarget?.name || ''
                        });
                      }}
                      label="Target"
                    >
                      {overflowTargets.map((target) => (
                        <MenuItem key={target.value} value={target.value}>
                          {target.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Alert severity="info">No targets available for this action</Alert>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setEditDialogOpen(false);
          }}>
            Cancel
          </Button>
          <Button onClick={handleSaveQueue} variant="contained">
            {editDialogOpen ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Queue</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete queue "{selectedQueue?.name}"? This will remove all members from the queue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteQueue} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Management Dialog */}
      <Dialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Members - {selectedQueue?.name}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <List>
            {extensions.map((ext) => (
              <ListItem key={ext.extension}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={!!ext.is_member}
                    onChange={() => handleToggleMember(ext)}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`${ext.extension} - ${ext.fullname || 'No Name'}`}
                  secondary={ext.is_member && ext.paused ? 'Paused' : ''}
                />
                {ext.is_member && (
                  <IconButton
                    edge="end"
                    onClick={() => handleTogglePause(ext)}
                    color={ext.paused ? 'warning' : 'success'}
                  >
                    {ext.paused ? <PlayArrowIcon /> : <PauseIcon />}
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default QueuesAsCustomerNew;
